// Legal pages — Terms, Privacy, Disclaimer, Refund.
// One file, one component, routed by slug.
import { useParams, Link, Navigate } from "react-router-dom";
import { Scale, ShieldCheck, AlertTriangle, RotateCcw } from "lucide-react";

const EFFECTIVE_DATE = "February 27, 2026";
const CONTACT_EMAIL = "registry@osrrglobal.com";
const CONTACT_PHONE = "(310) 905-7115";
const COMPANY_ADDRESS = "1624 S. Hope St. STE 301, Los Angeles, CA 90015, United States";
const CONTACT_LINE = `${CONTACT_EMAIL} · ${CONTACT_PHONE} · ${COMPANY_ADDRESS}`;

const LEGAL = {
  terms: {
    title: "Terms & Conditions",
    overline: "Legal · Service agreement",
    icon: Scale,
    sections: [
      {
        h: "1. Acceptance of these Terms",
        p: [
          "By accessing or using Over Seas Rights & Royalties (\"OSRR\", \"we\", \"us\"), available at this website and its sub-domains, you agree to be bound by these Terms & Conditions. If you do not agree, do not use the service.",
          "These Terms form a binding agreement between you and OSRR, a service operated from the address listed below.",
        ],
      },
      {
        h: "2. Description of service",
        p: [
          "OSRR is an informational registry and networking platform indexing musical-rights organizations, performing-rights societies (PROs), mechanical-rights organizations (MROs), publishers, sync agencies, branding firms, orchestration resources, and copyright authorities worldwide.",
          "OSRR is NOT itself a rights-collection society, a publisher, a record label, a law firm, or a payment-processor for royalties. We do not collect, administer, or distribute royalties on behalf of any user.",
        ],
      },
      {
        h: "3. Eligibility & accounts",
        p: [
          "You must be at least 18 years old (or the age of majority in your jurisdiction) to create an OSRR account. You agree to provide accurate, current, and complete information at registration and to keep it up to date.",
          "You are responsible for safeguarding your password and for all activity that occurs under your account. Notify us immediately by phone at " + CONTACT_PHONE + " of any unauthorized use.",
        ],
      },
      {
        h: "4. Complete Access — one-time payment",
        p: [
          "OSRR offers a one-time payment of US $6.95 to unlock \"Complete Access\" — a perpetual right to view the full agency directory, all three Rights Hubs, and the Translation Hub, subject to continued availability of the service. Payment is processed by Stripe; OSRR does not store your full card details.",
          "Stripe Checkout accepts cards and local payment methods from 195+ countries. Conversion to your local currency is performed by Stripe and/or your card issuer.",
          "\"Lifetime\" means for as long as OSRR continues to operate the service. If we discontinue the service, refunds are governed by our Refund Policy.",
        ],
      },
      {
        h: "5. Acceptable use",
        p: [
          "You agree NOT to: (a) scrape, harvest, or systematically copy the directory for resale; (b) misrepresent your identity or any third party; (c) upload unlawful, defamatory, or infringing content; (d) attempt to bypass the paywall or any technical limitation; (e) use the Translation Hub to translate content that is unlawful or that you do not have the right to translate.",
          "We may suspend or terminate any account that violates these Terms.",
        ],
      },
      {
        h: "6. Intellectual property",
        p: [
          "The OSRR name, logo, site design, code, curation choices, and aggregated directory layout are owned by OSRR. Individual third-party logos, agency names, and external links remain the property of their respective owners and are indexed for informational purposes under fair use.",
          "If you are a rights-holder and wish your entity removed from the directory, contact us by phone at " + CONTACT_PHONE + " and we will action the request promptly.",
        ],
      },
      {
        h: "7. Third-party links & content",
        p: [
          "OSRR contains links and embedded logos pointing to third-party websites (PROs, publishers, agencies, government authorities). We do not control, endorse, or warrant any third-party content, and we are not responsible for any loss arising from your use of third-party services.",
        ],
      },
      {
        h: "8. Disclaimers",
        p: [
          "OSRR provides information \"as is\" without warranties of any kind. See our separate Disclaimer page for details. Nothing on OSRR constitutes legal, financial, tax, or professional advice.",
        ],
      },
      {
        h: "9. Limitation of liability",
        p: [
          "To the maximum extent permitted by law, OSRR's total aggregate liability arising out of or in connection with the service shall not exceed the amount you paid us in the twelve (12) months preceding the claim — which for most users is US $6.95.",
          "We shall not be liable for indirect, incidental, consequential, special, punitive, or exemplary damages, including lost royalties, lost revenue, lost data, or business interruption.",
        ],
      },
      {
        h: "10. Indemnification",
        p: [
          "You agree to indemnify and hold OSRR harmless from any claim arising out of (a) your use of the service, (b) your violation of these Terms, or (c) your infringement of any third-party right.",
        ],
      },
      {
        h: "11. Termination",
        p: [
          "You may close your account at any time by contacting us at " + CONTACT_PHONE + ". We may suspend or terminate your account for breach of these Terms. Provisions that by their nature should survive termination (IP, disclaimers, limitation of liability) will survive.",
        ],
      },
      {
        h: "12. Governing law & disputes",
        p: [
          "These Terms are governed by the laws of the State of California, USA, without regard to conflict-of-laws principles. Any dispute shall be brought exclusively in the state or federal courts located in Los Angeles County, California, and you consent to the personal jurisdiction of those courts.",
        ],
      },
      {
        h: "13. Changes to these Terms",
        p: [
          "We may update these Terms from time to time. Material changes will be announced on the site. Continued use of the service after the effective date of the updated Terms constitutes acceptance of the changes.",
        ],
      },
      {
        h: "14. Contact",
        p: [
          "Questions about these Terms? Call us at " + CONTACT_PHONE + ".",
          "Mailing address: " + COMPANY_ADDRESS,
        ],
      },
    ],
  },

  privacy: {
    title: "Privacy Policy",
    overline: "Legal · Data & privacy",
    icon: ShieldCheck,
    sections: [
      {
        h: "1. Who we are",
        p: [
          "Over Seas Rights & Royalties (\"OSRR\", \"we\", \"us\") is the controller of personal data processed through this website. Contact: " + CONTACT_LINE + ".",
        ],
      },
      {
        h: "2. Data we collect",
        p: [
          "Account data: name, email, hashed password, country, role (composer/songwriter/etc.), bio, genres, avatar URL.",
          "Networking data: connection requests and saved/favourite agencies you bookmark.",
          "Payment data: when you purchase Complete Access we receive from Stripe a session ID, the amount, currency, and payment status. We do NOT receive or store your full card number, CVV, or expiry — those remain with Stripe.",
          "Usage data: standard server logs (IP address, user-agent, timestamp, requested URL) used for security and analytics.",
          "Translation Hub data: text you submit to the Translation Hub is sent to Anthropic (Claude) via our AI provider for the sole purpose of returning a translation; we do not retain the input or output beyond the lifetime of the request.",
        ],
      },
      {
        h: "3. How we use your data",
        p: [
          "To provide and operate the service (account, login, directory, networking, translation).",
          "To process your $6.95 Complete-Access purchase through Stripe.",
          "To respond to support requests received by phone at " + CONTACT_PHONE + ".",
          "To monitor, secure, and improve the service.",
        ],
      },
      {
        h: "4. Legal bases (GDPR / EEA users)",
        p: [
          "Performance of a contract: account creation, paywall purchase, directory access.",
          "Legitimate interest: security logging, fraud prevention, product analytics.",
          "Consent: optional features such as translation submissions, marketing emails (if any).",
          "Legal obligation: tax/accounting retention of payment records.",
        ],
      },
      {
        h: "5. Sharing & sub-processors",
        p: [
          "We share data only with sub-processors that help us operate the service:",
          "• Stripe Inc. — payment processing (USA).",
          "• MongoDB Atlas / our cloud infrastructure — database & hosting.",
          "• Anthropic (Claude) via our AI provider — Translation Hub processing only when you submit text.",
          "• Clearbit Logo API — automatically derives agency logos from public domain names.",
          "We do not sell your personal data to anyone.",
        ],
      },
      {
        h: "6. Cookies & local storage",
        p: [
          "We use a single httpOnly authentication cookie to keep you signed in. We also use local storage for UI preferences (language selector, search history within the session). We do not use third-party advertising cookies.",
        ],
      },
      {
        h: "7. International data transfers",
        p: [
          "Our servers and sub-processors are located primarily in the United States. By using the service from outside the U.S., you consent to the transfer of your data to the U.S. and other countries that may not provide the same level of protection as your home jurisdiction.",
        ],
      },
      {
        h: "8. Data retention",
        p: [
          "Account data: kept while your account is active and for up to 24 months after closure for legal/accounting purposes.",
          "Payment records: retained for the minimum period required by law (typically 7 years in the U.S.).",
          "Translation Hub submissions: not retained.",
        ],
      },
      {
        h: "9. Your rights",
        p: [
          "Depending on your jurisdiction, you may have the right to access, correct, delete, port, or restrict the processing of your personal data, and to object to processing or withdraw consent. Submit any request by phone to " + CONTACT_PHONE + " and we will respond within 30 days.",
          "California residents (CCPA/CPRA): you may request the categories of personal information collected, and request deletion. We do not sell personal information.",
          "EEA/UK residents (GDPR): you may lodge a complaint with your local data-protection authority.",
        ],
      },
      {
        h: "10. Security",
        p: [
          "Passwords are hashed with bcrypt; sessions use httpOnly cookies served over HTTPS. We take reasonable technical and organisational measures, but no internet service is 100% secure.",
        ],
      },
      {
        h: "11. Children",
        p: [
          "OSRR is not directed to children under 18. We do not knowingly collect personal data from children. If you believe we have inadvertently done so, contact us at " + CONTACT_PHONE + " and we will delete it.",
        ],
      },
      {
        h: "12. Changes",
        p: [
          "We may update this Privacy Policy. Material changes will be announced on the site, and the effective date below will be updated.",
        ],
      },
    ],
  },

  disclaimer: {
    title: "Disclaimer",
    overline: "Legal · No professional advice",
    icon: AlertTriangle,
    sections: [
      {
        h: "Informational purposes only",
        p: [
          "All content on Over Seas Rights & Royalties — including agency listings, descriptions, rights-topic articles, copyright-law summaries, branding directories, orchestration resources, and translations — is provided for general informational and networking purposes only.",
          "Nothing on OSRR constitutes legal, financial, tax, accounting, or other professional advice, and nothing creates an attorney-client, fiduciary, or advisor relationship between you and OSRR.",
        ],
      },
      {
        h: "No affiliation with listed agencies",
        p: [
          "OSRR indexes publicly available information about third-party rights organizations (ASCAP, BMI, SESAC, GMR, SACEM, GEMA, JASRAC, WIPO, the U.S. Copyright Office, USPTO, and 200+ other agencies worldwide).",
          "Inclusion of an organization in our directory does NOT imply any partnership, sponsorship, endorsement, or affiliation between that organization and OSRR. All trademarks, logos, and brand names are the property of their respective owners.",
        ],
      },
      {
        h: "No guarantee of accuracy",
        p: [
          "While we strive to keep the registry current, rights organizations restructure, rebrand, merge, or close. Phone numbers, addresses, websites, and rate schedules change without notice. We make no representation or warranty of any kind, express or implied, regarding the accuracy, completeness, currency, or reliability of any information on the site.",
          "Always verify critical information directly with the relevant agency before taking any action.",
        ],
      },
      {
        h: "No guarantee of royalty collection",
        p: [
          "OSRR is a directory and networking tool. We do NOT collect, administer, audit, or distribute royalties on your behalf. Your ability to collect royalties depends on your membership with the relevant PRO/MRO, the accuracy of your work registrations, and the policies of each collection society.",
          "Joining or contacting any agency listed on OSRR is at your own discretion and risk.",
        ],
      },
      {
        h: "Translation Hub disclaimer",
        p: [
          "The Translation Hub uses an AI language model (Claude) to translate text between dialects. AI translations may contain errors, miss nuance, or mistranslate technical or legal terminology. Do NOT use AI translations as a substitute for a certified human translator when executing binding contracts, registering works with foreign authorities, or in any legal proceeding.",
        ],
      },
      {
        h: "External links",
        p: [
          "OSRR contains links to external websites. We have no control over the content, privacy practices, or availability of these third-party sites and accept no responsibility for them.",
        ],
      },
      {
        h: "No warranty",
        p: [
          "The service is provided \"AS IS\" and \"AS AVAILABLE\" without warranty of any kind, whether express, implied, statutory, or otherwise, including the implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement.",
        ],
      },
      {
        h: "Limitation of liability",
        p: [
          "To the maximum extent permitted by law, in no event shall OSRR, its officers, employees, agents, or affiliates be liable for any direct, indirect, incidental, consequential, special, punitive, or exemplary damages — including lost royalties, lost revenue, lost data, or lost business — arising from your use of, or inability to use, the service. See our Terms & Conditions for the full liability cap.",
        ],
      },
    ],
  },

  refund: {
    title: "Refund Policy",
    overline: "Legal · Complete Access refunds",
    icon: RotateCcw,
    sections: [
      {
        h: "The $6.95 Complete Access purchase",
        p: [
          "Complete Access is a one-time payment of US $6.95 (or local-currency equivalent at the time of purchase, as calculated by Stripe) granting lifetime access to the OSRR directory and hubs.",
        ],
      },
      {
        h: "14-day refund window",
        p: [
          "If you change your mind, you may request a full refund within fourteen (14) days of your purchase by calling us at " + CONTACT_PHONE + " from a number where we can verify your account. Refunds are issued to the original payment method via Stripe and typically appear within 5–10 business days depending on your card issuer.",
        ],
      },
      {
        h: "After 14 days",
        p: [
          "Because the $6.95 fee grants lifetime access and the service is delivered immediately, purchases made more than 14 days ago are generally non-refundable. Exceptions may be considered on a case-by-case basis if the service was materially unavailable.",
        ],
      },
      {
        h: "Service discontinuation",
        p: [
          "If OSRR discontinues the Complete Access service entirely within 12 months of your purchase, we will refund the $6.95 on request without time limit. After 12 months, no refund obligation applies — the $6.95 represents fully delivered lifetime access.",
        ],
      },
      {
        h: "Chargebacks",
        p: [
          "Please call us first at " + CONTACT_PHONE + " before initiating a chargeback. We aim to resolve every legitimate refund request within 5 business days.",
        ],
      },
      {
        h: "Contact",
        p: [
          "Refund requests: " + CONTACT_PHONE + ".",
        ],
      },
    ],
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const doc = LEGAL[slug];
  if (!doc) return <Navigate to="/" replace />;

  const Icon = doc.icon;
  const otherPages = Object.entries(LEGAL).filter(([s]) => s !== slug);

  return (
    <div className="min-h-screen bg-[#0A1128] text-white pt-32 pb-20" data-testid={`legal-page-${slug}`}>
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/" className="overline text-slate-400 hover:text-white transition-colors" data-testid="legal-back">
          ← Back to OSRR
        </Link>

        <div className="mt-8 flex items-start gap-5">
          <div className="w-14 h-14 rounded-md border border-[#7DA3D9]/30 bg-[#7DA3D9]/5 flex items-center justify-center flex-shrink-0">
            <Icon className="w-6 h-6 text-[#7DA3D9]" />
          </div>
          <div>
            <div className="overline" style={{ color: "#7DA3D9" }}>{doc.overline}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2">{doc.title}</h1>
            <p className="text-sm text-slate-400 mt-3">
              Effective date: <span className="text-slate-200">{EFFECTIVE_DATE}</span>
            </p>
          </div>
        </div>

        <article className="mt-14 space-y-10" data-testid="legal-body">
          {doc.sections.map((s, i) => (
            <section key={i} className="space-y-3">
              <h2 className="font-display text-xl lg:text-2xl text-white tracking-tight">{s.h}</h2>
              {s.p.map((para, j) => (
                <p key={j} className="text-slate-300 leading-relaxed text-[15px]">
                  {para}
                </p>
              ))}
            </section>
          ))}
        </article>

        <div className="mt-20 pt-10 border-t border-white/10">
          <div className="overline mb-4">Related legal documents</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="legal-related">
            {otherPages.map(([s, d]) => {
              const RIcon = d.icon;
              return (
                <Link
                  key={s}
                  to={`/legal/${s}`}
                  className="flex items-center gap-3 p-4 rounded-md border border-white/10 hover:border-[#7DA3D9]/40 hover:bg-white/[0.03] transition-colors"
                  data-testid={`legal-related-${s}`}
                >
                  <RIcon className="w-4 h-4 text-[#7DA3D9]" />
                  <span className="text-sm text-slate-200">{d.title}</span>
                </Link>
              );
            })}
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-14 leading-relaxed">
          Over Seas Rights &amp; Royalties · {COMPANY_ADDRESS} · {CONTACT_PHONE} · {CONTACT_EMAIL}
        </p>
      </div>
    </div>
  );
}
